/**
 * Cart Brinde Grátis (auto-add gift at threshold)
 *
 * Quando o subtotal dos itens PAGOS atinge o threshold, adiciona automaticamente
 * o variant do brinde (a R$0 via desconto automático do Shopify) com uma line item
 * property marcadora pra o desconto casar. Remove o brinde se o subtotal pago cair
 * abaixo do threshold.
 *
 * Segue o padrão vanilla do order bump: fetch /cart/add.js + dispatch
 * CartAddEvent ('cart:update') pra o cart-drawer re-renderizar.
 *
 * Config 100% por settings (Theme Editor) — injetada via window.HoomyBrinde no
 * snippet cart-brinde-gratis-config.liquid.
 */
(function () {
  const cfg = window.HoomyBrinde || {};

  // Gate: só roda se ativado e com variant configurado.
  if (!cfg.enabled || !cfg.variantId || !cfg.threshold) return;

  const VARIANT_ID = String(cfg.variantId);
  const THRESHOLD_CENTS = Math.round(Number(cfg.threshold) * 100);
  const PROP_KEY = cfg.propKey || '_brinde';
  const PROP_VALUE = cfg.propValue || 'mascara-2000';

  // Evita reentrância: nossas próprias mutações disparam 'cart:update'.
  // `pending` coalesce pedidos que chegam durante uma execução em curso, pra
  // NENHUM cart:update ser perdido (ex.: cliques rápidos no quantity selector).
  // Sem isso, o 2º evento caía no guard e era dropado, deixando a máscara grátis
  // no carrinho abaixo do limiar por segundos até um próximo evento.
  let busy = false;
  let pending = false;

  /**
   * Soma o subtotal dos itens PAGOS (exclui a linha do brinde).
   * Usa final_line_price (já considera descontos de linha) pra ficar consistente.
   * @param {any} cart
   * @returns {number} centavos
   */
  function paidSubtotal(cart) {
    let total = 0;
    for (const item of cart.items) {
      if (isGift(item)) continue;
      total += item.final_line_price;
    }
    return total;
  }

  /**
   * @param {any} item
   * @returns {boolean} true se a linha é o brinde adicionado por nós
   */
  function isGift(item) {
    const props = item.properties || {};
    return props[PROP_KEY] === PROP_VALUE;
  }

  /**
   * Retorna a primeira line item do brinde, ou null.
   * @param {any} cart
   */
  function findGift(cart) {
    return cart.items.find(isGift) || null;
  }

  async function getCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function addGift() {
    const body = {
      items: [
        {
          id: VARIANT_ID,
          quantity: 1,
          properties: { [PROP_KEY]: PROP_VALUE },
        },
      ],
    };
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Falha ao adicionar brinde');
    return res.json();
  }

  async function removeGift(giftItem) {
    // change.js por key zera a linha sem mexer em índices.
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
    });
    if (!res.ok) throw new Error('Falha ao remover brinde');
    return res.json();
  }

  /**
   * Notifica o tema pra re-renderizar o cart drawer (progress bar inclusa).
   * @param {any} cartData
   */
  function notifyCartUpdate(cartData) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          source: 'cart-brinde-gratis',
          resource: cartData,
          data: { source: 'cart-brinde-gratis', itemCount: cartData.item_count },
        },
      })
    );
  }

  async function reconcile() {
    // Coalescência: se já há um reconcile rodando, marca que outro é necessário
    // e sai — o que está rodando vai re-executar no fim. Assim nenhuma mudança de
    // carrinho é perdida e o estado sempre converge pro subtotal mais recente.
    if (busy) {
      pending = true;
      return;
    }
    busy = true;
    try {
      do {
        pending = false;

        // Sempre relê o cart fresco do servidor a cada passada — é a fonte da
        // verdade do subtotal qualificável (mesma base da barra e do selo).
        const cart = await getCart();
        const gift = findGift(cart);
        const subtotal = paidSubtotal(cart);

        if (subtotal >= THRESHOLD_CENTS) {
          // Qualifica: garante exatamente 1 brinde.
          if (!gift) {
            const updated = await addGift();
            notifyCartUpdate(updated);
          } else if (gift.quantity > 1) {
            // Rede de segurança: qty do brinde foi forçada pra >1 por outro caminho
            // (API direta, outro script). Normaliza pra 1 sem janela de zero usando
            // change.js direto — sem remover + re-adicionar, sem perder a property.
            const res = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: gift.key, quantity: 1 }),
            });
            if (!res.ok) throw new Error('Falha ao normalizar qty do brinde');
            const updated = await res.json();
            notifyCartUpdate(updated);
          }
        } else if (gift) {
          // Desqualificou: remove o brinde de forma confiável.
          const updated = await removeGift(gift);
          notifyCartUpdate(updated);
        }
        // Se chegou outro cart:update enquanto processávamos, roda de novo.
      } while (pending);
    } catch (err) {
      console.error('[cart-brinde-gratis]', err);
    } finally {
      busy = false;
    }
  }

  // Reage a qualquer mudança de carrinho (add, qty, remove).
  document.addEventListener('cart:update', (e) => {
    // Ignora os eventos que nós mesmos disparamos (source em qualquer um dos dois
    // níveis), pra não entrar em loop com o nosso próprio notifyCartUpdate.
    const detail = (e && e.detail) || {};
    const src = detail.source || (detail.data && detail.data.source);
    if (src === 'cart-brinde-gratis') return;
    reconcile();
  });

  // Estado inicial no load (carrinho persistido pode já qualificar/desqualificar).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
